<template>
  <div>
    <section id="enterprise-grid-map" :class="{ 'slide-map': isSlide }">
      <div>
        <map-view-grid-item
          :vesselPositions="vesselPositions"
          :mapId="'grid-map-1'"
          :top="'0vh'"
          :left="'0vw'"
        />
        <map-view-grid-item
          :vesselPositions="vesselPositions"
          :mapId="'grid-map-2'"
          :top="'50vh'"
          :left="'0vw'"
        />
        <map-view-grid-item
          :vesselPositions="vesselPositions"
          :mapId="'grid-map-3'"
          :top="'0vh'"
          :left="'50vw'"
        />
        <map-view-grid-item
          :vesselPositions="vesselPositions"
          :mapId="'grid-map-4'"
          :top="'50vh'"
          :left="'50vw'"
        />
      </div>
    </section>
    <div class="map-controls-si">
      <right-menu-base
        :isActiveMenu="isActiveMenu"
        :closeSidebar="closeSidebar"
        :keyDownCloseSetting="keyDownCloseSetting"
      >
        <template v-slot:header>
          <menu-bar
            :menuTitle="displayMenu.menuTitle"
            :closeSidebar="closeSidebar"
          />
        </template>
        <template v-slot:main>
          <component
            :is="displayMenu.component"
            :map="map"
            :settings="scrollSettings"
            :vesselPositions="vesselPositions"
            :enterpriseId="enterpriseId"
          />
        </template>
      </right-menu-base>
    </div>
  </div>
</template>

<script>
/**
 * ４画面マップと各画面の右メニューを表示するコンポーネント
 * @parent P.MapView
 */
import MapViewGridItem from "@/components/organisms/MapViewGridItem";
import RightMenuBase from "@/components/organisms/RightMenuBase";
import { createNamespacedHelpers, mapGetters } from "vuex";
import { RightMenuMixin } from "@/mixins";
import { KEY_CODE } from "@/config/const.js";
import { db } from "@/firebase/firebaseConfig.js";
/** 各メニューのorganismsがインポートされる */
import MenuBar from "@/components/molecules/MenuBar";
import OwnVessel from "@/components/organisms/OwnVessel";
import SelectedVessel from "@/components/organisms/SelectedVessel";
import FilterMap from "@/components/organisms/FilterMap";
import Plot from "@/components/organisms/Plot";
import PrivatePlot from "@/components/organisms/PrivatePlot";
import Settings from "@/components/organisms/Settings";
import EquipmentBaseStation from "@/components/organisms/EquipmentBaseStation";
import AnchorVessel from "@/components/organisms/AnchorVessel";
import AuthService from "@/services/authService";

const { mapState: mapStateOfLiveMap, mapActions: mapActionsOfLiveMap } =
  createNamespacedHelpers("liveMap");

const {
  mapState: mapStateOfGridMap,
  mapGetters: mapGettersOfGridMap,
  mapMutations: mapMutationsOfGridMap,
  mapActions: mapActionsOfGridMap,
} = createNamespacedHelpers("gridMap");

const { mapActions: mapActionsOfPrivatePlot, mapState: mapStateOfPrivatePlot } =
  createNamespacedHelpers("gkmgPrivatePlot");

const { mapActions: mapActionsOfAnchor } =
  createNamespacedHelpers("gkmgAnchor");

const {
  mapActions: mapActionsOfVesselEquipmentBaseStation,
  mapState: mapStateOfVesselEquipmentBaseStation,
} = createNamespacedHelpers("gkmgVesselEquipmentBaseStation");

export default {
  name: "T.MapViewGridMain",
  components: {
    MapViewGridItem,
    RightMenuBase,
    OwnVessel,
    SelectedVessel,
    FilterMap,
    Settings,
    Plot,
    PrivatePlot,
    EquipmentBaseStation,
    AnchorVessel,
    MenuBar,
  },
  mixins: [RightMenuMixin],
  data() {
    return {
      map: null,
      // FireStoreのリッスン解放
      unsubscribes: [],
      keyDownCloseSetting: {
        isEnable: true,
        keyCode: KEY_CODE.ESC,
      },
      displayMenu: {
        menuId: null,
        menuTitle: "",
        component: "",
      },
      /** スクロールの設定 */
      scrollSettings: {
        suppressScrollX: true,
        maxScrollbarLength: 60,
        wheelSpeed: 0.6,
      },
      // vesselId・aisInfo・vesselPositionを格納
      vesselPositions: [],
      // 時間経過によるプライベートプロットの監視
      intervalIdPrivatePlot: null,
      enterpriseId: null,
    };
  },
  watch: {
    "activeSidebarForGrid.isActive": function (newValue) {
      if (newValue) {
        this.displayMenu =
          this.menuObject[this.activeSidebarForGrid.componentName];
      }
    },
    "activeSidebarForGrid.componentName": function (newValue) {
      if (this.activeSidebarForGrid.isActive) {
        this.displayMenu = this.menuObject[newValue];
      }
    },
    selectedVessels: {
      immediate: true,
      deep: true,
      handler: function (newVessels) {
        this.addOnSnapShotForVessels(newVessels);
      },
    },
    ownVessels: {
      immediate: true,
      deep: true,
      handler: function (newVessels) {
        this.addOnSnapShotForVessels(newVessels);
      },
    },
    equipmentVessels: {
      immediate: true,
      deep: true,
      handler: function (newVessels) {
        const newEquipmentVessels = newVessels.reduce((acc, vessel) => {
          if (vessel && vessel.vessel_id) {
            acc.push({ id: vessel.vessel_id });
          }
          return acc;
        }, []);
        this.addOnSnapShotForVessels(newEquipmentVessels);
      },
    },
  },
  computed: {
    ...mapGettersOfGridMap(["rightMenuForGridSettingTarget"]),
    ...mapStateOfLiveMap({
      selectedVessels: (state) => state.monitoringVessels,
      ownVessels: (state) => state.ownVessels,
      // お気に入りラベルとグループリストを紐づけたリスト
      favoriteList: (state) => state.favoriteList,
    }),
    ...mapStateOfGridMap({
      // 右メニューの状態管理
      activeSidebarForGrid: (state) => state.activeSidebarForGrid,
    }),
    ...mapStateOfPrivatePlot({
      privatePlots: (state) => state.privatePlots,
    }),
    ...mapStateOfVesselEquipmentBaseStation({
      equipmentVessels: (state) => state.equipmentVessels,
    }),
    ...mapGetters({
      getAddonFlag: "getAddonFlag",
      vesselPositionCollection: "vesselPositionCollection",
    }),
    isSlide() {
      return ["grid-map-3", "grid-map-4"].includes(
        this.rightMenuForGridSettingTarget,
      );
    },
    isActiveMenu() {
      const status = this.activeSidebarForGrid.isActive;
      return status;
    },
  },
  methods: {
    ...mapActionsOfLiveMap([
      "checkFirestoreDataReady",
      "createFirestoreData",
      "getFavoriteLabels", // お気に入りラベルを取得
      "fetchGroups", // 選択船をフェッチ
      "fetchOwnVessels", // 自主船をフェッチ
      "fetchCountryCode",
      "setOwnVesselDisplayDurationTimeFromLocalDb",
    ]),
    ...mapActionsOfPrivatePlot([
      "fetchPrivatePlots",
      "removePrivatePlots",
      "createPrivatePlotFeatures",
    ]),
    ...mapActionsOfAnchor(["fetchAnchors", "removeAnchor"]),
    ...mapActionsOfVesselEquipmentBaseStation([
      "fetchEquipmentBaseStations",
      "removeEquipmentBaseStations",
      "fetchEquipmentVessels",
      "removeEquipmentVessels",
      "fetchEquipmentCategories",
      "removeEquipmentCategories",
      "fetchEquipmentDepartureWharfs",
      "removeEquipmentDepartureWharfs",
      "fetchEquipmentDestinations",
      "removeEquipmentDestinations",
      "fetchEquipmentTypes",
      "removeEquipmentTypes",
      "fetchEquipmentSeaRoutes",
      "removeEquipmentSeaRoutes",
    ]),
    ...mapActionsOfGridMap(["resetGridMapState"]),
    ...mapMutationsOfGridMap(["SET_ACTIVE_SIDEBAR_FOR_GRID"]),
    // サイドバーを閉じる
    closeSidebar() {
      this.SET_ACTIVE_SIDEBAR_FOR_GRID({
        mapId: "",
        isActive: false,
        componentName: "",
      });
    },
    // 船舶のonSnapShotを追加
    addOnSnapShotForVessels(newVessels) {
      const existIds = new Set(
        this.vesselPositions.map((vesselPosition) => {
          return vesselPosition.vesselId;
        }),
      );
      newVessels
        .filter((vessel) => !existIds.has(vessel.id))
        .forEach((vessel) => {
          this.vesselPositions.push({
            vesselId: vessel.id,
            vesselPosition: null,
            aisInfo: null,
            onSnapshotFlag: false,
          });
        });
      // 取得したvesselIdでポジションをリッスンする
      for (const vessel of this.vesselPositions) {
        // 既にリッスン開始している場合は処理しない
        if (!vessel.onSnapshotFlag) {
          vessel.onSnapshotFlag = true;
          this.subscribeAisInfo(vessel.vesselId);
        }
      }
    },
    // fireStoreのvessel_positionをリッスン
    async subscribeVesselPosition(vesselId) {
      let unsubscribe = await db
        .collection(this.vesselPositionCollection)
        .doc(vesselId)
        .onSnapshot(
          (doc) => {
            if (doc.data() !== undefined) {
              for (const vessel of this.vesselPositions) {
                if (vessel.vesselId === vesselId) {
                  vessel.vesselPosition = doc.data();
                }
              }
            }
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    // fireStoreのvessel_info(自動船舶識別装置)をリッスン
    async subscribeAisInfo(vesselId) {
      let unsubscribe = await db
        .collection("vessel_info")
        .doc(vesselId)
        .onSnapshot(
          (doc) => {
            if (doc.data() !== undefined) {
              for (const vessel of this.vesselPositions) {
                if (vessel.vesselId === vesselId) {
                  vessel.aisInfo = doc.data();
                  this.subscribeVesselPosition(vesselId);
                }
              }
            }
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    /**
     * アンカーのスナップショットを取得
     */
    async onSnapshotAnchors(enterpriseId) {
      let query = db.collection("gkmg_vessel_anchor_points");
      query = query.where("deleted_at", "==", null);
      // 法人IDで絞り込む
      query = query.where("enterprise_id", "==", enterpriseId);
      let unsubscribe = await query.onSnapshot(
        (snapshot) => {
          // 取得結果が0件のときにアンカーが更新されるようにするための対応
          if (snapshot.size === 0) {
            this.fetchAnchors(null);
            return;
          }
          snapshot.docChanges().forEach((change) => {
            if (change.doc.data() !== undefined) {
              if (change.type === "removed") {
                this.removeAnchor(change.doc.data());
              } else {
                this.fetchAnchors({
                  ...change.doc.data(),
                  hash: change.doc.id,
                });
              }
            }
          });
        },
        function (_) {},
      );
      this.unsubscribes.push(unsubscribe);
    },
    // 拠点のスナップショット
    async onSnapshotEquipmentBaseStations() {
      let unsubscribe = await db
        .collection("gkmg_equipment_base_stations")
        .onSnapshot(
          (snapshot) => {
            if (snapshot.size === 0) {
              this.fetchEquipmentBaseStations(null);
              return;
            }
            snapshot.docChanges().forEach(async (change) => {
              if (change.doc.data() !== undefined) {
                if (change.type === "removed") {
                  this.removeEquipmentBaseStations(change.doc.id);
                } else {
                  const baseRecord = await change.doc.ref
                    .collection("data")
                    .get();
                  this.fetchEquipmentBaseStations({
                    ...change.doc.data(),
                    hash: change.doc.id,
                    data: baseRecord.docs.map((doc) => {
                      return {
                        ...doc.data(),
                        hash: doc.id,
                      };
                    }),
                  });
                }
              }
            });
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    // 資機材船舶のスナップショット
    async onSnapshotEquipmentVessels() {
      let unsubscribe = await db
        .collection("gkmg_equipment_vessels")
        .onSnapshot(
          (snapshot) => {
            if (snapshot.size === 0) {
              this.fetchEquipmentVessels(null);
              return;
            }
            snapshot.docChanges().forEach(async (change) => {
              if (change.doc.data() !== undefined) {
                if (change.type === "removed") {
                  this.removeEquipmentVessels(change.doc.id);
                } else {
                  const vesselRecord = await change.doc.ref
                    .collection("data")
                    .limit(1)
                    .get();
                  // vesselRecord.sizeが0になる場合があるのでvesselRecord.sizeが存在する場合に実行する。
                  if (vesselRecord.size) {
                    this.fetchEquipmentVessels({
                      ...change.doc.data(),
                      hash: change.doc.id,
                      data: vesselRecord.docs[0].data(),
                    });
                  }
                }
              }
            });
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    // 資機材カテゴリマスタのスナップショット
    async onSnapshotEquipmentCategories() {
      let unsubscribe = await db
        .collection("gkmg_equipment_categories")
        .onSnapshot(
          (snapshot) => {
            if (snapshot.size === 0) {
              this.fetchEquipmentCategories(null);
              return;
            }
            snapshot.docChanges().forEach((change) => {
              if (change.doc.data() !== undefined) {
                if (change.type === "removed") {
                  this.removeEquipmentCategories(change.doc.id);
                } else {
                  this.fetchEquipmentCategories({
                    ...change.doc.data(),
                    hash: change.doc.id,
                  });
                }
              }
            });
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    // 出発岸壁マスタのスナップショット
    async onSnapshotEquipmentDepartureWharfs() {
      let unsubscribe = await db
        .collection("gkmg_equipment_departure_wharfs")
        .onSnapshot(
          (snapshot) => {
            if (snapshot.size === 0) {
              this.fetchEquipmentDepartureWharfs(null);
              return;
            }
            snapshot.docChanges().forEach((change) => {
              if (change.doc.data() !== undefined) {
                if (change.type === "removed") {
                  this.removeEquipmentDepartureWharfs(change.doc.id);
                } else {
                  this.fetchEquipmentDepartureWharfs({
                    ...change.doc.data(),
                    hash: change.doc.id,
                  });
                }
              }
            });
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    // 目的地マスタのスナップショット
    async onSnapshotEquipmentDestinations() {
      let unsubscribe = await db
        .collection("gkmg_equipment_destinations")
        .onSnapshot(
          (snapshot) => {
            if (snapshot.size === 0) {
              this.fetchEquipmentDestinations(null);
              return;
            }
            snapshot.docChanges().forEach((change) => {
              if (change.doc.data() !== undefined) {
                if (change.type === "removed") {
                  this.removeEquipmentDestinations(change.doc.id);
                } else {
                  this.fetchEquipmentDestinations({
                    ...change.doc.data(),
                    hash: change.doc.id,
                  });
                }
              }
            });
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    // 投入実績のスナップショット
    async onSnapshotEquipmentTypes() {
      let unsubscribe = await db.collection("gkmg_equipment_types").onSnapshot(
        (snapshot) => {
          if (snapshot.size === 0) {
            this.fetchEquipmentTypes(null);
            return;
          }
          snapshot.docChanges().forEach(async (change) => {
            if (change.doc.data() !== undefined) {
              if (change.type === "removed") {
                this.removeEquipmentTypes(change.doc.id);
              } else {
                const trackRecord = await change.doc.ref
                  .collection("data")
                  .limit(1)
                  .get();
                this.fetchEquipmentTypes({
                  ...change.doc.data(),
                  hash: change.doc.id,
                  data:
                    trackRecord.size === 0 ? null : trackRecord.docs[0].data(),
                });
              }
            }
          });
        },
        function (_) {},
      );
      this.unsubscribes.push(unsubscribe);
    },
    // 運搬航路のスナップショット
    async onSnapshotEquipmentSeaRoutes() {
      let unsubscribe = await db.collection("gkmg_sea_routes").onSnapshot(
        (snapshot) => {
          if (snapshot.size === 0) {
            this.fetchEquipmentSeaRoutes(null);
            return;
          }
          snapshot.docChanges().forEach(async (change) => {
            if (change.doc.data() !== undefined) {
              if (change.type === "removed") {
                this.removeEquipmentSeaRoutes(change.doc.id);
              } else {
                const baseRecord = await change.doc.ref
                  .collection("data")
                  .get();
                this.fetchEquipmentSeaRoutes({
                  ...change.doc.data(),
                  hash: change.doc.id,
                  data: baseRecord.docs.map((doc) => {
                    return {
                      ...doc.data(),
                      hash: doc.id,
                    };
                  }),
                });
              }
            }
          });
        },
        function (_) {},
      );
      this.unsubscribes.push(unsubscribe);
    },
    // プライベートプロットのスナップショット
    async onSnapshotPrivatePlots() {
      let unsubscribe = await db
        .collection("gkmg_private_plots")
        .where("deleted_at", "==", null)
        .onSnapshot(
          (snapshot) => {
            // 取得結果が0件のときにプライベートプロットが更新されるようにするための対応
            if (snapshot.size === 0) {
              this.fetchPrivatePlots(null);
              return;
            }
            snapshot.docChanges().forEach((change) => {
              if (change.doc.data() !== undefined) {
                // データをセットする際に、ハッシュ値が含まれるようにする。
                const param = {
                  ...change.doc.data(),
                  hash: change.doc.id,
                };
                if (change.type === "removed") {
                  this.removePrivatePlots(param);
                } else {
                  this.fetchPrivatePlots(param);
                }
              }
            });
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
  },
  async created() {
    const user = await AuthService.getUser();
    this.enterpriseId = user.id;
    // FireStoreにお気に入りグループが存在するか確認
    const isFavoriteListReady = await this.checkFirestoreDataReady();
    // FireStoreにお気に入りグループが存在しない場合は作成
    if (!isFavoriteListReady) await this.createFirestoreData();
    // 並列処理で実行する関数
    const functionArray = [
      this.getFavoriteLabels(),
      this.fetchGroups(),
      this.fetchOwnVessels(),
      this.fetchCountryCode(),
      this.setOwnVesselDisplayDurationTimeFromLocalDb(),
    ];
    // プライベートプロットのアドオンがtrueの場合はsnapshot実行
    if (this.getAddonFlag("GKMG_PRIVATE_PLOT")) {
      functionArray.push(this.onSnapshotPrivatePlots());
      this.intervalIdPrivatePlot = setInterval(() => {
        this.createPrivatePlotFeatures(this.privatePlots);
      }, 60000);
    }
    if (this.getAddonFlag("GKMG_ANCHOR_MANAGEMENT")) {
      functionArray.push(this.onSnapshotAnchors(this.enterpriseId));
    }
    if (this.getAddonFlag("GKMG_BASE_STATION")) {
      functionArray.push(this.onSnapshotEquipmentBaseStations());
      functionArray.push(this.onSnapshotEquipmentVessels());
      functionArray.push(this.onSnapshotEquipmentCategories());
      functionArray.push(this.onSnapshotEquipmentDepartureWharfs());
      functionArray.push(this.onSnapshotEquipmentDestinations());
      functionArray.push(this.onSnapshotEquipmentTypes());
      functionArray.push(this.onSnapshotEquipmentSeaRoutes());
    }
    await Promise.all(functionArray);
  },
  beforeDestroy() {
    this.resetGridMapState();
    // プライベートプロットのアドオンがtrueの場合はintervalを解放
    if (this.getAddonFlag("GKMG_PRIVATE_PLOT")) {
      clearInterval(this.intervalIdPrivatePlot);
    }
    if (this.unsubscribes.length !== 0) {
      this.unsubscribes.forEach((unsubscribe) => unsubscribe());
      this.unsubscribes = [];
    }
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";
#enterprise-grid-map {
  position: relative;
  right: 0;
  transition: all 0.4s ease-out 0s;
  canvas {
    cursor: crosshair;
  }
}

#enterprise-grid-map.slide-map {
  right: 400px;
}

#grid-map-controls-si {
  z-index: 2;
}
</style>
